import { z } from "zod";
import { ProseSchema } from "./prose.js";
/**
 * P2a Triage output (docs/prompt_pack.md P2a, ADR 0004). A discriminated
 * union on `intent` — each variant carries only what it needs, and there is
 * no fallthrough variant: every value of `intent` names exactly one shape.
 *
 * `AMBIGUOUS` corresponds to prompt_pack.md's "return CLARIFY with a
 * clarifying_question" instruction. This task lists AMBIGUOUS as its own
 * intent value, distinct from CLARIFY, which is a cleaner discriminant than
 * prompt_pack.md's literal text (which overloads CLARIFY to mean both "an
 * answerable question" and "an ambiguous message") — implemented as directed
 * by this task; the prompt_pack.md wording itself is unchanged and still
 * describes the ambiguous case as "return CLARIFY ... and nothing else".
 *
 * Field typing policy: every payload below that quotes the LEARNER's own
 * words (question, statement, declared_intent, recommendation) is a plain
 * string, not Prose — Prose's banned-vocabulary filter exists to keep
 * SYSTEM-authored output mechanics-free (Law 2); applying it to the
 * learner's own free text would reject ordinary English ("let me check the
 * numbers") that happens to overlap the banned list. `clarifying_question`
 * on AMBIGUOUS is the one exception: that text is Triage's own, authored,
 * system-generated question back to the learner, so it is Prose.
 */
export const TriageResultSchema = z.discriminatedUnion("intent", [
    z.strictObject({
        intent: z.literal("CLARIFY"),
        question: z.string().min(1),
    }),
    z.strictObject({
        intent: z.literal("REQUEST_DOCUMENT"),
        /** A resolved document id, or a known data-room channel name — never both, never neither. */
        target: z.union([
            z.strictObject({ kind: z.literal("document_id"), document_id: z.string().min(1) }),
            z.strictObject({ kind: z.literal("channel"), channel: z.string().min(1) }),
        ]),
    }),
    z.strictObject({
        intent: z.literal("STATE_FRAMEWORK"),
        statement: z.string().min(1),
    }),
    z.strictObject({
        intent: z.literal("ACT"),
        declared_intent: z.string().min(1),
    }),
    z.strictObject({
        intent: z.literal("CHALLENGE_RULING"),
        disputed_ruling_turn: z.number().int().min(0),
    }),
    z.strictObject({
        intent: z.literal("SUBMIT_RECOMMENDATION"),
        recommendation: z.string().min(1),
    }),
    /**
     * The learner answering a deal on the table (decision 2). Valid only while
     * one is pending — the engine treats either as a plain move otherwise. The
     * rationale is the learner's own words, kept for the Assessor.
     */
    z.strictObject({
        intent: z.literal("ACCEPT_BARGAIN"),
        rationale: z.string().min(1),
    }),
    z.strictObject({
        intent: z.literal("DECLINE_BARGAIN"),
        rationale: z.string().min(1),
    }),
    z.strictObject({
        intent: z.literal("META"),
        request: z.enum(["settings", "help", "quit"]),
    }),
    z.strictObject({
        intent: z.literal("AMBIGUOUS"),
        clarifying_question: ProseSchema,
    }),
]);
//# sourceMappingURL=triage-result.js.map